/* eslint-disable no-console */
/* eslint-disable import/no-extraneous-dependencies */
import { Span, Tracer } from "@opentelemetry/api";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { Resource, resourceFromAttributes } from "@opentelemetry/resources";
import {
  WebTracerProvider,
  TraceIdRatioBasedSampler,
  Sampler,
} from "@opentelemetry/sdk-trace-web";
import { LoggerProvider } from "@opentelemetry/sdk-logs";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { ITelemetryService } from "./core";
import { StartActiveSpanConfig } from "./core/types";
import { OtelServiceOptions } from "./types";

export class OTelService implements ITelemetryService {
  private readonly config: OtelServiceOptions;

  private tracerProvider?: WebTracerProvider;

  private loggerProvider?: LoggerProvider;

  private tracer?: Tracer;

  private initialized = false;

  constructor(config?: OtelServiceOptions) {
    this.config = config || {};
  }

  initialize() {
    if (this.initialized) return;

    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: this.config.serviceName || "web-app",
      ...this.config.resourceAttributes,
    });

    const sampler = new TraceIdRatioBasedSampler(
      this.config.samplingRatio ?? 1.0,
    );

    this.tracerProvider = this.setupTracerProvider(resource, sampler);
    this.loggerProvider = this.setupLoggerProvider(resource);

    registerInstrumentations({
      tracerProvider: this.tracerProvider,
      loggerProvider: this.loggerProvider,
      instrumentations: this.config.instruments,
    });

    this.initialized = true;
  }

  private setupTracerProvider(
    resource: Resource,
    sampler: Sampler,
  ): WebTracerProvider {
    const contextManager = new ZoneContextManager();

    const spanProcessors =
      this.config.otelTracerProvider?.flatMap(
        (p) => p.setup({ resource })?.spanProcessors || [],
      ) ?? [];

    const tracerProvider = new WebTracerProvider({
      resource,
      sampler,
      spanProcessors,
    });

    tracerProvider.register({
      contextManager,
      propagator: new W3CTraceContextPropagator(),
    });

    return tracerProvider;
  }

  private setupLoggerProvider(resource: Resource): LoggerProvider {
    const logProcessors =
      this.config.otelLoggerProvider?.flatMap(
        (p) => p.setup()?.logRecordProcessors || [],
      ) ?? [];

    const loggerProvider = new LoggerProvider({
      resource,
      processors: logProcessors,
    });

    return loggerProvider;
  }

  setupTracer(name: string, version?: string) {
    if (!this.tracerProvider) return;
    this.tracer = this.tracerProvider.getTracer(name, version);
  }

  startActiveSpan<T>(
    config: StartActiveSpanConfig,
    callback: (span?: Span) => T,
  ): T {
    if (!this.tracer) return callback();
    const { spanName, options, context } = config;
    if (context && options)
      return this.tracer.startActiveSpan(spanName, options, context, callback);
    if (options)
      return this.tracer.startActiveSpan(spanName, options, callback);
    return this.tracer.startActiveSpan(spanName, callback);
  }

  getLogger(name: string): void {
    if (!this.loggerProvider) return;
    this.loggerProvider.getLogger(name);
  }
}

export function createOtel(config: OtelServiceOptions) {
  return new OTelService(config);
}
